import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {Button} from '@interstate/components/Button';
import {TextInput} from '@interstate/components/TextInput';
import {Alert} from '@interstate/components/Alert';
import type {Role} from '@ait-tracker/shared';
import {useAuth} from '../context/AuthContext';
import {
  api,
  ApiError,
  type AdminDomain,
  type DiffDomain,
  type ManagedUser,
  type Portfolio,
  type UploadDiff
} from '../lib/api';
import {LoadingState, ErrorState} from '../components/States';
import {ValidationReport} from '../components/ValidationReport';
import {checkSnapshotFileText, type FileCheck} from '../lib/validate';

const ALL_ROLES: Role[] = ['admin', 'portfolio-viewer', 'domain-owner'];
const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  'portfolio-viewer': 'Portfolio Viewer',
  'domain-owner': 'Domain Owner'
};
const UNASSIGNED = '_unassigned';

type Tab = 'upload' | 'portfolios' | 'domains' | 'users';

/**
 * Admin management console (project ADR-0006 — admin-only). Upload the snapshot, define portfolios
 * and which domains belong to them, and manage users + their scope (portfolios and/or domains for
 * viewers, domains for owners). The API enforces authorization on every action; this UI is the
 * admin surface for it.
 *
 * UX model: each tab's resting state is a single table. Creating and editing happen in one
 * right-side drawer (create = edit with blanks), so a row carries a single "Edit" affordance and
 * the full record — including scope pickers — lives in one place.
 */
export default function Manage(): React.ReactElement {
  const {user, logout} = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('upload');
  const [domains, setDomains] = useState<AdminDomain[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // The initial load shows the full-page spinner; post-save refetches are `silent` so a save
  // updates the tables in place (via ajax) without blanking the console to a loading state.
  const load = useCallback(async (opts?: {silent?: boolean}) => {
    if (!opts?.silent) setLoading(true);
    setError('');
    try {
      const [{domains: ds}, {portfolios: ps}, {users: us}] = await Promise.all([
        api.listDomains(),
        api.listPortfolios(),
        api.listUsers()
      ]);
      setDomains(ds);
      setPortfolios(ps);
      setUsers(us);
    } catch (err) {
      // Only blank to the error state on the initial load; a failed background refetch keeps the
      // chris-v1 data (the mutation's own handler surfaces its error).
      if (!opts?.silent) setError(err instanceof ApiError ? err.message : 'Failed to load management data.');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  // Passed to panels as `onChange`: refetch quietly after a save, no full-page spinner.
  const refresh = useCallback(() => load({silent: true}), [load]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-700 text-white flex items-center justify-center text-sm font-extrabold">E</div>
            <div>
              <div className="text-sm font-bold text-gray-900">Management Console</div>
              <div className="text-xs text-gray-400">{user?.displayName} · admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button buttonStyle="secondary" size="small" onClick={() => navigate('/dashboard')}>
              Dashboard
            </Button>
            <Button buttonStyle="secondary" size="small" onClick={() => void logout()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-12 py-8">
        <nav className="flex gap-1 mb-6 border-b border-gray-200">
          {(['upload', 'portfolios', 'domains', 'users'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-semibold capitalize border-b-2 -mb-px transition-colors ${
                tab === t ? 'border-blue-700 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {t}
            </button>
          ))}
        </nav>

        {loading ? (
          <LoadingState label="Loading management data…" />
        ) : error ? (
          <ErrorState message={error} onRetry={() => void refresh()} />
        ) : (
          <>
            {tab === 'upload' && <UploadPanel onChange={refresh} />}
            {tab === 'portfolios' && <PortfoliosPanel portfolios={portfolios} domains={domains} onChange={refresh} />}
            {tab === 'domains' && <DomainsPanel domains={domains} portfolios={portfolios} onChange={refresh} />}
            {tab === 'users' && <UsersPanel users={users} domains={domains} portfolios={portfolios} onChange={refresh} />}
          </>
        )}
      </main>
    </div>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────────────────

/** A section card. `action` renders on the right of the title (e.g. the "＋ New" button). */
function Card({title, action, children}: {title: string; action?: React.ReactNode; children: React.ReactNode}): React.ReactElement {
  return (
    <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4 gap-4">
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Map a portfolio id to its display name (falls back to the id; the reserved bucket → "Uncategorized"). */
function nameFor(portfolios: Portfolio[], id: string): string {
  if (id === UNASSIGNED) return 'Uncategorized';
  return portfolios.find(p => p.portfolioId === id)?.name ?? id;
}

const toggle = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter(v => v !== value) : [...list, value];

/** Order-independent set equality for the scope arrays (avoids a redundant save call). */
const sameSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every(v => b.includes(v));

/** Read-only chips (a scope summary in a table cell). */
function Chips({values}: {values: string[]}): React.ReactElement {
  if (values.length === 0) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {values.map(v => (
        <span key={v} className="inline-block bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 text-xs">
          {v}
        </span>
      ))}
    </div>
  );
}

// ── Right-side drawer (the single create/edit surface) ───────────────────────────

/**
 * A right-hand slide-over. The table stays visible behind it. `footer` lays out its own actions
 * (put a destructive action first, then a `flex-1` spacer, then Cancel/Save).
 */
function Drawer({
  title,
  subtitle,
  children,
  footer,
  onClose
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  onClose: () => void;
}): React.ReactElement {
  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-label={title}
        className="absolute right-0 top-0 h-full w-[480px] max-w-[94vw] bg-white shadow-xl flex flex-col"
      >
        <div className="px-6 h-16 flex items-center justify-between border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-900 truncate">{title}</h3>
            {subtitle && <div className="text-xs text-gray-400 font-mono truncate">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none px-2 shrink-0" aria-label="Close">
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        <div className="px-6 h-16 flex items-center gap-2 border-t border-gray-200 shrink-0">{footer}</div>
      </div>
    </div>
  );
}

// ── Upload ─────────────────────────────────────────────────────────────────────

function UploadPanel({onChange}: {onChange: () => Promise<void>}): React.ReactElement {
  const [fileName, setFileName] = useState('');
  const [check, setCheck] = useState<FileCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{type: 'success' | 'error'; text: string; details?: string[]} | null>(null);
  // Two-phase (spec 003): pick+validate → preview diff → confirm capture.
  const [diff, setDiff] = useState<UploadDiff | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const ready = check?.status === 'valid';

  const reset = () => {
    setCheck(null);
    setFileName('');
    setDiff(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  // Read + client-validate a chosen file (from the picker or a drop). Shared by both entry points.
  const handleFile = async (f: File | undefined) => {
    setMsg(null);
    setDiff(null);
    setCheck(null);
    if (!f) {
      setFileName('');
      return;
    }
    setFileName(f.name);
    setCheck(checkSnapshotFileText(await f.text()));
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void handleFile(e.dataTransfer.files?.[0]);
  };

  // Phase 1 → 2: stage the file and fetch the diff (no writes yet).
  const preview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) {
      setMsg({type: 'error', text: 'Pick a file that passes validation first.'});
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const d = await api.previewSnapshot(check.parsed);
      setDiff(d);
    } catch (err) {
      setMsg({
        type: 'error',
        text: err instanceof ApiError ? err.message : 'Could not preview the upload.',
        details: err instanceof ApiError ? err.details : undefined
      });
    } finally {
      setBusy(false);
    }
  };

  // Phase 2 → commit: capture the previewed upload (renames carry over automatically by id).
  const capture = async () => {
    if (!diff) return;
    const renamedNote = diff.renamed.length > 0 ? ` ${diff.renamed.length} renamed in place.` : '';
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.commitSnapshot(diff.uploadId);
      setMsg({
        type: 'success',
        text: `Captured ${res.domainCount} domains at ${new Date(res.uploadedAt).toLocaleString()}.${renamedNote} New domains start uncategorized — group them on the Portfolios tab.`
      });
      reset();
      await onChange();
    } catch (err) {
      setMsg({
        type: 'error',
        text: err instanceof ApiError ? err.message : 'Capture failed.',
        details: err instanceof ApiError ? err.details : undefined
      });
    } finally {
      setBusy(false);
    }
  };

  const alert = msg && (
    <Alert type={msg.type === 'success' ? 'success' : 'error'} sx={{marginBottom: '1rem'}}>
      {msg.text}
      {msg.details && msg.details.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-xs">
          {msg.details.slice(0, 12).map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      )}
    </Alert>
  );

  // ── Phase 2: review the diff before capture ──
  if (diff) {
    return (
      <Card title="Review changes before capture">
        {alert}
        <UploadDiffReview diff={diff} />
        <div className="mt-6 flex items-center gap-3">
          <Button buttonStyle="primary" size="small" disabled={busy} onClick={() => void capture()}>
            {busy ? 'Capturing…' : 'Confirm & capture'}
          </Button>
          <Button buttonStyle="secondary" size="small" disabled={busy} onClick={reset}>
            Cancel
          </Button>
        </div>
      </Card>
    );
  }

  // ── Phase 1: pick + validate ──
  return (
    <Card title="Upload the snapshot">
      <form onSubmit={preview} className="max-w-xl">
        {alert}
        <p className="text-sm text-gray-500 mb-3">
          One snapshot for the whole org. You'll see a diff — added, removed, and renamed domains —
          and confirm it before anything is captured. Existing domains keep their portfolio; domains
          no longer present are retired.
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1">Snapshot file (JSON)</label>
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={e => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`mb-3 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-6 py-8 text-center cursor-pointer transition-colors ${
            dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }`}
        >
          <span className="text-sm text-gray-700">
            {fileName ? (
              <>
                Selected <span className="font-semibold">{fileName}</span> — drop another to replace
              </>
            ) : (
              <>
                <span className="font-semibold text-blue-700">Choose a file</span> or drag &amp; drop it here
              </>
            )}
          </span>
          <span className="text-xs text-gray-400">JSON only</span>
          <input
            ref={inputRef}
            type="file"
            accept="application/json,.json"
            onChange={e => void handleFile(e.target.files?.[0])}
            className="hidden"
          />
        </div>

        {check && (
          <div className="mb-4">
            <ValidationReport check={check} fileName={fileName} />
          </div>
        )}

        <div className="mt-4">
          <Button buttonStyle="primary" type="submit" size="small" disabled={busy || !ready}>
            {busy ? 'Analyzing…' : 'Preview changes'}
          </Button>
          {check && !ready && <span className="ml-3 text-xs text-gray-500">Fix the issues above to enable preview.</span>}
        </div>
      </form>
    </Card>
  );
}

/** The added / removed / renamed / unchanged diff (spec 003). Domains are keyed by their stable id,
 *  so renames are shown as certainties — the domain keeps its portfolio + owner grants on capture. */
function UploadDiffReview({diff}: {diff: UploadDiff}): React.ReactElement {
  const noChanges = diff.added.length === 0 && diff.removed.length === 0 && diff.renamed.length === 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2 text-xs">
        <Stat label="Added" value={diff.added.length} tone="green" />
        <Stat label="Removed" value={diff.removed.length} tone={diff.removed.length > 0 ? 'red' : 'gray'} />
        <Stat label="Renamed" value={diff.renamed.length} tone={diff.renamed.length > 0 ? 'blue' : 'gray'} />
        <Stat label="Unchanged" value={diff.unchanged.length} tone="gray" />
      </div>

      {diff.removed.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">Removed — will be retired</h4>
          <p className="text-xs text-gray-500 mb-2">
            These domains are gone from the file. Each is retired (its data stops being served);
            annotations show what a removal costs.
          </p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {diff.removed.map(r => (
              <li key={r.domainId} className="px-3 py-2">
                <div className="text-sm text-gray-900">{r.name}</div>
                <div className="text-[11px] text-gray-500">
                  {r.portfolio && r.portfolio !== UNASSIGNED ? `Portfolio: ${r.portfolio} · ` : ''}
                  {(r.ownerGrantCount ?? 0) > 0 ? `${r.ownerGrantCount} owner grant(s) will go dark` : 'no owner grants'}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {diff.renamed.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">Renamed — kept in place</h4>
          <p className="text-xs text-gray-500 mb-2">
            Same domain id, new name. Portfolio classification and owner grants carry over automatically.
          </p>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {diff.renamed.map(r => (
              <li key={r.domainId} className="px-3 py-2 text-sm text-gray-900">
                <span className="text-gray-500 line-through">{r.previousName}</span>
                <span className="mx-2 text-gray-400">→</span>
                <span className="font-medium">{r.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {diff.added.length > 0 && (
        <DiffList title="New domains" hint="Start uncategorized — group them on the Portfolios tab." items={diff.added} />
      )}

      {noChanges && (
        <p className="text-sm text-gray-500">No domain changes — capturing refreshes the snapshot chrome and version history.</p>
      )}
    </div>
  );
}

function DiffList({title, hint, items}: {title: string; hint?: string; items: DiffDomain[]}): React.ReactElement | null {
  if (items.length === 0) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 mb-1">
        {title} ({items.length})
      </h4>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <ul className="flex flex-wrap gap-1.5">
        {items.map(d => (
          <li key={d.domainId} className="text-xs bg-gray-100 text-gray-700 rounded px-2 py-0.5">
            {d.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stat({label, value, tone}: {label: string; value: number; tone: 'green' | 'red' | 'blue' | 'gray'}): React.ReactElement {
  const tones: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
    blue: 'bg-blue-100 text-blue-800',
    gray: 'bg-gray-100 text-gray-600'
  };
  return (
    <span className={`rounded px-2 py-1 font-semibold ${tones[tone]}`}>
      {value} {label}
    </span>
  );
}

// ── Inline scope pickers (used inside drawers) ───────────────────────────────────

/** A flat portfolio checklist (short list → always expanded). */
function PortfolioChecklist({
  portfolios,
  selected,
  onChange
}: {
  portfolios: Portfolio[];
  selected: string[];
  onChange: (next: string[]) => void;
}): React.ReactElement {
  if (portfolios.length === 0) {
    return <p className="text-sm text-gray-400">No portfolios defined yet.</p>;
  }
  return (
    <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto">
      {portfolios.map(p => (
        <li key={p.portfolioId}>
          <label className="flex items-center gap-2 text-sm px-3 py-2 cursor-pointer hover:bg-gray-50">
            <input type="checkbox" checked={selected.includes(p.portfolioId)} onChange={() => onChange(toggle(selected, p.portfolioId))} />
            <span className="font-medium text-gray-700">{p.name}</span>
            <span className="text-xs text-gray-400 ml-auto">{p.domainCount}</span>
          </label>
        </li>
      ))}
    </ul>
  );
}

/** A filterable domain checklist grouped by portfolio (defined portfolios first, uncategorized last). */
function DomainChecklist({
  domains,
  portfolios,
  selected,
  onChange
}: {
  domains: AdminDomain[];
  portfolios: Portfolio[];
  selected: string[];
  onChange: (next: string[]) => void;
}): React.ReactElement {
  const [filter, setFilter] = useState('');

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (d: AdminDomain) => !q || d.name.toLowerCase().includes(q) || d.domainId.includes(q);
    const order = [...portfolios.map(p => p.portfolioId), UNASSIGNED];
    return order
      .map(id => ({
        id,
        name: nameFor(portfolios, id),
        items: domains.filter(d => d.portfolio === id && match(d)).sort((a, b) => a.name.localeCompare(b.name))
      }))
      .filter(g => g.items.length > 0);
  }, [domains, portfolios, filter]);

  return (
    <>
      <input
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="Filter domains…"
        className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-full mb-2"
      />
      <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto p-3">
        {groups.length === 0 ? (
          <p className="text-sm text-gray-400">No matching domains.</p>
        ) : (
          <div className="space-y-4">
            {groups.map(g => (
              <div key={g.id}>
                <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1.5">{g.name}</div>
                <ul className="space-y-1">
                  {g.items.map(d => (
                    <li key={d.domainId}>
                      <label className="flex items-center gap-2 text-sm text-gray-700 py-0.5 cursor-pointer">
                        <input type="checkbox" checked={selected.includes(d.domainId)} onChange={() => onChange(toggle(selected, d.domainId))} />
                        <span>{d.name}</span>
                        <span className="text-xs text-gray-300 font-mono">{d.domainId}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="text-xs text-gray-400 mt-1">{selected.length} selected</div>
    </>
  );
}

// ── Portfolios (table + drawer) ──────────────────────────────────────────────────

function PortfoliosPanel({
  portfolios,
  domains,
  onChange
}: {
  portfolios: Portfolio[];
  domains: AdminDomain[];
  onChange: () => Promise<void>;
}): React.ReactElement {
  // null = closed, 'new' = create, Portfolio = edit that row.
  const [editing, setEditing] = useState<Portfolio | 'new' | null>(null);
  const [msg, setMsg] = useState<{type: 'success' | 'error'; text: string} | null>(null);

  return (
    <>
      <Card
        title={`Portfolios (${portfolios.length})`}
        action={
          <Button buttonStyle="primary" size="small" onClick={() => setEditing('new')}>
            ＋ New portfolio
          </Button>
        }
      >
        {msg && (
          <Alert type={msg.type === 'success' ? 'success' : 'error'} sx={{marginBottom: '1rem'}}>
            {msg.text}
          </Alert>
        )}
        {portfolios.length === 0 ? (
          <p className="text-sm text-gray-400">No portfolios yet — create one with “＋ New portfolio”.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200">
                  <th className="py-2 pr-4 font-semibold">Portfolio</th>
                  <th className="py-2 pr-4 font-semibold">ID</th>
                  <th className="py-2 pr-4 font-semibold">Domains</th>
                  <th className="py-2 pr-4 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {portfolios.map(p => (
                  <tr key={p.portfolioId} className="border-b border-gray-100">
                    <td className="py-2.5 pr-4 text-gray-800">
                      <div className="font-medium">{p.name}</div>
                      {p.description && <div className="text-xs text-gray-400">{p.description}</div>}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-400 font-mono text-xs">{p.portfolioId}</td>
                    <td className="py-2.5 pr-4 text-gray-600">{p.domainCount}</td>
                    <td className="py-2.5 pr-4 text-right">
                      <Button buttonStyle="secondary" size="small" onClick={() => setEditing(p)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <PortfolioDrawer
          key={editing === 'new' ? 'new' : editing.portfolioId}
          portfolio={editing === 'new' ? null : editing}
          domains={domains}
          portfolios={portfolios}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChange();
          }}
          onError={t => setMsg({type: 'error', text: t})}
        />
      )}
    </>
  );
}

/** Create/edit a portfolio: name, description, and its domain membership — plus delete (edit only). */
function PortfolioDrawer({
  portfolio,
  domains,
  portfolios,
  onClose,
  onSaved,
  onError
}: {
  portfolio: Portfolio | null;
  domains: AdminDomain[];
  portfolios: Portfolio[];
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (text: string) => void;
}): React.ReactElement {
  const isNew = portfolio === null;
  const [name, setName] = useState(portfolio?.name ?? '');
  const [description, setDescription] = useState(portfolio?.description ?? '');
  const initialDomains = useMemo(
    () => (portfolio ? domains.filter(d => d.portfolio === portfolio.portfolioId).map(d => d.domainId) : []),
    [portfolio, domains]
  );
  const [domainIds, setDomainIds] = useState<string[]>(initialDomains);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        const created = await api.createPortfolio({name: name.trim(), description: description.trim() || undefined});
        if (domainIds.length > 0) await api.setPortfolioDomains(created.portfolioId, domainIds);
      } else {
        await api.updatePortfolio(portfolio.portfolioId, {name: name.trim(), description: description.trim()});
        if (!sameSet(domainIds, initialDomains)) await api.setPortfolioDomains(portfolio.portfolioId, domainIds);
      }
      await onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Save failed.');
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!portfolio) return;
    if (!window.confirm(`Delete "${portfolio.name}"? Its ${portfolio.domainCount} domain(s) become uncategorized.`)) return;
    setBusy(true);
    try {
      await api.deletePortfolio(portfolio.portfolioId);
      await onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Delete failed.');
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={isNew ? 'New portfolio' : `Edit ${portfolio.name}`}
      subtitle={isNew ? undefined : portfolio.portfolioId}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <Button buttonStyle="secondary" size="small" disabled={busy} onClick={() => void remove()}>
              Delete
            </Button>
          )}
          <span className="flex-1" />
          <Button buttonStyle="secondary" size="small" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button buttonStyle="primary" size="small" disabled={busy || name.trim().length === 0} onClick={() => void save()}>
            {busy ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <TextInput name="pname" label="Name" value={name} onChange={e => setName(e.target.value)} required />
        <TextInput name="pdesc" label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Domains in this portfolio</label>
          {domains.length === 0 ? (
            <p className="text-sm text-gray-400">No domains yet — upload a snapshot first.</p>
          ) : (
            <DomainChecklist domains={domains} portfolios={portfolios} selected={domainIds} onChange={setDomainIds} />
          )}
        </div>
      </div>
    </Drawer>
  );
}

// ── Domains (single-domain classify into a defined portfolio) ────────────────────

function DomainsPanel({
  domains,
  portfolios,
  onChange
}: {
  domains: AdminDomain[];
  portfolios: Portfolio[];
  onChange: () => Promise<void>;
}): React.ReactElement {
  const [msg, setMsg] = useState<{type: 'success' | 'error'; text: string} | null>(null);
  const uncategorized = domains.filter(d => d.portfolio === UNASSIGNED).length;

  return (
    <Card title={`Domains (${domains.length})`}>
      {msg && (
        <Alert type={msg.type === 'success' ? 'success' : 'error'} sx={{marginBottom: '1rem'}}>
          {msg.text}
        </Alert>
      )}
      {uncategorized > 0 && (
        <Alert type="caution" sx={{marginBottom: '1rem'}}>
          {uncategorized} {uncategorized === 1 ? 'domain is' : 'domains are'} uncategorized. Uncategorized
          domains are visible to admins only — Portfolio Viewers won't see them until they're assigned a
          portfolio below.
        </Alert>
      )}
      {domains.length === 0 ? (
        <p className="text-sm text-gray-400">No domains yet — upload a snapshot first.</p>
      ) : portfolios.length === 0 ? (
        <p className="text-sm text-gray-400">Create a portfolio first, then assign domains to it here or on the Portfolios tab.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200">
                <th className="py-2 pr-4 font-semibold">Domain</th>
                <th className="py-2 pr-4 font-semibold">ID</th>
                <th className="py-2 pr-4 font-semibold">Portfolio</th>
                <th className="py-2 pr-4 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {domains.map(d => (
                <DomainRow
                  key={d.domainId}
                  domain={d}
                  portfolios={portfolios}
                  onSaved={onChange}
                  onError={t => setMsg({type: 'error', text: t})}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function DomainRow({
  domain,
  portfolios,
  onSaved,
  onError
}: {
  domain: AdminDomain;
  portfolios: Portfolio[];
  onSaved: () => Promise<void>;
  onError: (text: string) => void;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  // Optimistic selection: reflect the pick immediately (dropdown locks while it saves), then let the
  // silent refetch reconcile the prop. Revert if the save fails.
  const [selected, setSelected] = useState(domain.portfolio);
  useEffect(() => setSelected(domain.portfolio), [domain.portfolio]);

  const change = async (portfolioId: string) => {
    if (!portfolioId || portfolioId === selected) return;
    const prev = selected;
    setSelected(portfolioId);
    setBusy(true);
    try {
      await api.setDomainPortfolio(domain.domainId, portfolioId);
      await onSaved();
    } catch (err) {
      setSelected(prev);
      onError(err instanceof ApiError ? err.message : 'Failed to set portfolio.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${domain.name}"? It reappears on the next upload if still in the file.`)) return;
    setBusy(true);
    try {
      await api.deleteDomain(domain.domainId);
      await onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Failed to delete domain.');
    } finally {
      setBusy(false);
    }
  };

  const isUncategorized = selected === UNASSIGNED;

  return (
    <tr className="border-b border-gray-100">
      <td className="py-2.5 pr-4 text-gray-800">
        {domain.name}
        {isUncategorized && (
          <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
            Uncategorized
          </span>
        )}
      </td>
      <td className="py-2.5 pr-4 text-gray-400 font-mono text-xs">{domain.domainId}</td>
      <td className="py-2.5 pr-4">
        <div className="flex items-center gap-2">
          <select
            value={isUncategorized ? '' : selected}
            disabled={busy}
            onChange={e => void change(e.target.value)}
            className={`border rounded-md px-2 py-1 text-sm w-56 bg-white disabled:opacity-60 disabled:cursor-wait ${
              isUncategorized ? 'border-amber-400 ring-1 ring-amber-200' : 'border-gray-300'
            }`}
          >
            <option value="">Uncategorized</option>
            {portfolios.map(p => (
              <option key={p.portfolioId} value={p.portfolioId}>
                {p.name}
              </option>
            ))}
          </select>
          {busy && <span className="text-xs text-gray-400">Saving…</span>}
        </div>
      </td>
      <td className="py-2.5 pr-4 text-right">
        <button
          type="button"
          onClick={() => void remove()}
          disabled={busy}
          className="text-xs text-red-600 hover:text-red-800 hover:underline disabled:opacity-50"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ── Users (table + drawer) ─────────────────────────────────────────────────────

function UsersPanel({
  users,
  domains,
  portfolios,
  onChange
}: {
  users: ManagedUser[];
  domains: AdminDomain[];
  portfolios: Portfolio[];
  onChange: () => Promise<void>;
}): React.ReactElement {
  const [msg, setMsg] = useState<{type: 'success' | 'error'; text: string} | null>(null);
  // null = closed, 'new' = create, ManagedUser = edit that row.
  const [editing, setEditing] = useState<ManagedUser | 'new' | null>(null);

  return (
    <>
      <Card
        title={`Users (${users.length})`}
        action={
          <Button buttonStyle="primary" size="small" onClick={() => setEditing('new')}>
            ＋ New user
          </Button>
        }
      >
        {msg && (
          <Alert type={msg.type === 'success' ? 'success' : 'error'} sx={{marginBottom: '1rem'}}>
            {msg.text}
          </Alert>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200">
                <th className="py-2 pr-4 font-semibold">Name</th>
                <th className="py-2 pr-4 font-semibold">Email</th>
                <th className="py-2 pr-4 font-semibold">Roles</th>
                <th className="py-2 pr-4 font-semibold">Portfolios</th>
                <th className="py-2 pr-4 font-semibold">Domains</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-3 text-sm text-gray-400">
                    No users yet.
                  </td>
                </tr>
              ) : (
                users.map(u => (
                  <tr key={u.email} className="border-b border-gray-100 align-top">
                    <td className="py-2.5 pr-4 text-gray-800">{u.displayName}</td>
                    <td className="py-2.5 pr-4 text-gray-500 font-mono text-xs">{u.email}</td>
                    <td className="py-2.5 pr-4 text-gray-700">{u.roles.map(r => ROLE_LABELS[r] ?? r).join(', ')}</td>
                    <td className="py-2.5 pr-4">
                      <Chips values={u.assignedPortfolios.map(id => nameFor(portfolios, id))} />
                    </td>
                    <td className="py-2.5 pr-4">
                      <Chips values={u.assignedDomainIds.map(id => domains.find(d => d.domainId === id)?.name ?? id)} />
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className={u.enabled ? 'text-gray-600' : 'text-amber-700 font-medium'}>
                        {u.enabled ? 'enabled' : 'disabled'}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-right">
                      <Button buttonStyle="secondary" size="small" onClick={() => setEditing(u)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <UserDrawer
          key={editing === 'new' ? 'new' : editing.email}
          user={editing === 'new' ? null : editing}
          domains={domains}
          portfolios={portfolios}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onChange();
          }}
          onError={t => setMsg({type: 'error', text: t})}
        />
      )}
    </>
  );
}

/**
 * Create/edit a user in one surface: identity, roles, status, and — for viewers/owners — the
 * portfolio/domain scope inline. Authentication is Entra SSO (ADR-0007), so there is no credential
 * to set here; the email must match the user's Entra identity. There is no delete endpoint for
 * users; disabling is the reversible off-switch (checkbox on edit).
 */
function UserDrawer({
  user,
  domains,
  portfolios,
  onClose,
  onSaved,
  onError
}: {
  user: ManagedUser | null;
  domains: AdminDomain[];
  portfolios: Portfolio[];
  onClose: () => void;
  onSaved: () => Promise<void>;
  onError: (text: string) => void;
}): React.ReactElement {
  const isNew = user === null;
  const [email, setEmail] = useState(user?.email ?? '');
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [roles, setRoles] = useState<Role[]>(user?.roles ?? ['portfolio-viewer']);
  const [enabled, setEnabled] = useState(user?.enabled ?? true);
  const [assignedPortfolios, setAssignedPortfolios] = useState<string[]>(user?.assignedPortfolios ?? []);
  const [assignedDomainIds, setAssignedDomainIds] = useState<string[]>(user?.assignedDomainIds ?? []);
  const [busy, setBusy] = useState(false);

  const isViewer = roles.includes('portfolio-viewer');
  const isOwner = roles.includes('domain-owner');

  const canSave =
    displayName.trim().length > 0 && roles.length > 0 && (!isNew || email.trim().length > 0);

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await api.createUser({
          email: email.trim(),
          displayName: displayName.trim(),
          roles,
          assignedPortfolios: isViewer ? assignedPortfolios : [],
          assignedDomainIds: isViewer || isOwner ? assignedDomainIds : []
        });
      } else {
        await api.updateUser(user.email, {
          displayName: displayName.trim(),
          roles,
          enabled,
          assignedPortfolios: isViewer ? assignedPortfolios : [],
          assignedDomainIds: isViewer || isOwner ? assignedDomainIds : []
        });
      }
      await onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Save failed.');
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={isNew ? 'New user' : `Edit ${user.displayName}`}
      subtitle={isNew ? undefined : user.email}
      onClose={onClose}
      footer={
        <>
          <span className="flex-1" />
          <Button buttonStyle="secondary" size="small" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button buttonStyle="primary" size="small" disabled={busy || !canSave} onClick={() => void save()}>
            {busy ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {isNew ? (
          <TextInput name="email" label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        ) : (
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">Email</span>
            <span className="text-sm text-gray-500 font-mono">{user.email}</span>
          </div>
        )}

        <TextInput name="dn" label="Display name" value={displayName} onChange={e => setDisplayName(e.target.value)} required />

        <CheckboxRow
          label="Roles"
          options={ALL_ROLES}
          selected={roles}
          labelFor={r => ROLE_LABELS[r as Role] ?? r}
          onToggle={r => setRoles(prev => toggle(prev, r))}
        />

        {!isNew && (
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="checkbox" checked={enabled} onChange={() => setEnabled(v => !v)} /> Enabled
          </label>
        )}

        {isViewer && (
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1.5">
              Assigned portfolios <span className="font-normal text-gray-400">— sees every domain in these</span>
            </span>
            <PortfolioChecklist portfolios={portfolios} selected={assignedPortfolios} onChange={setAssignedPortfolios} />
          </div>
        )}

        {(isViewer || isOwner) && (
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1.5">
              Assigned domains{' '}
              <span className="font-normal text-gray-400">
                — {isViewer ? 'extra individual domains beyond the portfolios above' : 'sees exactly these'}
              </span>
            </span>
            <DomainChecklist domains={domains} portfolios={portfolios} selected={assignedDomainIds} onChange={setAssignedDomainIds} />
          </div>
        )}
      </div>
    </Drawer>
  );
}

// ── Small presentational helper ───────────────────────────────────────────────

function CheckboxRow<T extends string>({
  label,
  options,
  selected,
  onToggle,
  labelFor
}: {
  label: string;
  options: T[];
  selected: T[];
  onToggle: (value: T) => void;
  labelFor?: (value: T) => string;
}): React.ReactElement {
  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1.5">{label}</span>
      {options.length === 0 ? (
        <span className="text-xs text-gray-400">None available.</span>
      ) : (
        <div className="flex flex-wrap gap-3">
          {options.map(opt => (
            <label key={opt} className="inline-flex items-center gap-1.5 text-sm text-gray-700">
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => onToggle(opt)} />
              {labelFor ? labelFor(opt) : opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
